import {
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	CheckBox as CheckBoxIcon,
	CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
	IndeterminateCheckBox as IndeterminateCheckBoxIcon
} from "@mui/icons-material";
import { Box, Button, Divider, Menu, MenuItem, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";

export interface ITableFilterDropdownMenuItem {
	id: number;
	label: string;
}

export interface ITableFilterDropdownPropsBase {
	title: string;
	menuItems: ITableFilterDropdownMenuItem[];
	minWidth?: number;
	hideSelectAll?: boolean;
	capitalizeTitle?: boolean;
}

export interface ITableFilterDropdownPropsSingle extends ITableFilterDropdownPropsBase {
	multiple?: false;
	onChange: (value: number) => void;
}

export interface ITableFilterDropdownPropsMultiple extends ITableFilterDropdownPropsBase {
	multiple: true;
	onChange: (value: number[]) => void;
}

type ITableFilterDropdownProps = ITableFilterDropdownPropsSingle | ITableFilterDropdownPropsMultiple;

const TableFilterDropdown: FC<ITableFilterDropdownProps> = (props) => {
	const { title, menuItems, minWidth = 100, hideSelectAll, capitalizeTitle, multiple, onChange } = props;

	const dropdownAnchorRef = useRef<HTMLDivElement>(null);

	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
	const [selectedMenuItem, setSelectedMenuItem] = useState<ITableFilterDropdownMenuItem | null>(null);

	const [selectedMenuItemsList, setSelectedMenuItemsList] = useState<ITableFilterDropdownMenuItem[]>([]);
	const [finalSelectedItems, setFinalSelectedItems] = useState<ITableFilterDropdownMenuItem[]>([]);

	function handleOpenDropdown() {
		setDropdownOpen(true);
	}

	function handleCloseDropdown(success?: boolean) {
		setDropdownOpen(false);

		if (multiple && !success) {
			setSelectedMenuItemsList((finalSelectedItems.length !== 0 && [...finalSelectedItems]) || [...menuItems]);
		}
	}

	function handleChange(value: ITableFilterDropdownMenuItem | null) {
		if (!multiple) onChange(value ? value.id : -1);
		setSelectedMenuItem(value);
		handleCloseDropdown();
	}

	function handleChangeMultiSelectItems(value: ITableFilterDropdownMenuItem | null) {
		setSelectedMenuItemsList((currentSelection) => {
			if (!value) {
				if (currentSelection.length === menuItems.length) return [];
				return [...menuItems];
			}

			const updatedSelection = [...currentSelection];

			if (updatedSelection.some((item) => item.id === value.id)) {
				return updatedSelection.filter((item) => item.id !== value.id);
			}

			updatedSelection.push(value);
			return updatedSelection;
		});
	}

	function handleSubmitMultiSelectItems() {
		if (multiple) onChange(selectedMenuItemsList.map((item) => item.id));
		setFinalSelectedItems([...selectedMenuItemsList]);
		handleCloseDropdown(true);
	}

	useEffect(() => {
		if (multiple) {
			setFinalSelectedItems((currentSelection) => {
				if (currentSelection.length === menuItems.length) return currentSelection;
				return [...menuItems];
			});

			setSelectedMenuItemsList((currentSelection) => {
				if (currentSelection.length === menuItems.length) return currentSelection;
				return [...menuItems];
			});
		}
	}, [menuItems, multiple]);

	if (multiple) {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="multi-select-dropdown"
					aria-controls="multi-select-dropdown-menu"
					aria-haspopup
					aria-expanded={dropdownOpen}
					onClick={handleOpenDropdown}
					ref={dropdownAnchorRef}
				>
					<Typography
						variant="subtitle2"
						overflow="hidden"
						textOverflow="ellipsis"
						textTransform={capitalizeTitle ? "capitalize" : "none"}
					>
						{title}
					</Typography>

					{dropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="multi-select-dropdown-menu"
					anchorEl={dropdownAnchorRef.current}
					open={dropdownOpen}
					onClose={() => handleCloseDropdown()}
					MenuListProps={{ "aria-labelledby": "multi-select-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeMultiSelectItems(null)} sx={{ gap: "0.5rem" }}>
						{selectedMenuItemsList.length === 0 ? (
							<CheckBoxOutlineBlankIcon color="primary" />
						) : selectedMenuItemsList.length === menuItems.length ? (
							<CheckBoxIcon color="primary" />
						) : (
							<IndeterminateCheckBoxIcon color="primary" />
						)}
						Select All
					</MenuItem>

					<Divider />

					{menuItems.map((item) => (
						<MenuItem key={item.id} onClick={() => handleChangeMultiSelectItems(item)} sx={{ gap: "0.5rem" }}>
							{selectedMenuItemsList.some((selectedItem) => selectedItem.id === item.id) ? (
								<CheckBoxIcon color="primary" />
							) : (
								<CheckBoxOutlineBlankIcon color="primary" />
							)}

							{item.label}
						</MenuItem>
					))}

					<Divider />

					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: "0.5rem" }}>
						<Button onClick={handleSubmitMultiSelectItems}>Apply</Button>
					</Box>
				</Menu>
			</Box>
		);
	}

	return (
		<Box>
			<Box
				component="div"
				className="table-column-header-dropdown-wrapper"
				id="table-filter-dropdown"
				aria-controls="table-filter-dropdown-menu"
				aria-haspopup
				aria-expanded={dropdownOpen}
				onClick={handleOpenDropdown}
				ref={dropdownAnchorRef}
			>
				<Typography
					variant="subtitle2"
					overflow="hidden"
					textOverflow="ellipsis"
					textTransform={capitalizeTitle ? "capitalize" : "none"}
				>
					{selectedMenuItem ? selectedMenuItem.label : title}
				</Typography>

				{dropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
			</Box>

			<Menu
				id="table-filter-dropdown-menu"
				anchorEl={dropdownAnchorRef.current}
				open={dropdownOpen}
				onClose={() => handleCloseDropdown()}
				MenuListProps={{ "aria-labelledby": "table-filter-dropdown" }}
				sx={{ width: "100%" }}
			>
				{!hideSelectAll ? (
					<MenuItem selected={!selectedMenuItem} sx={{ minWidth }} onClick={() => handleChange(null)}>
						<em>All</em>
					</MenuItem>
				) : null}

				{menuItems.map((item) => (
					<MenuItem
						selected={!!selectedMenuItem && selectedMenuItem.id === item.id}
						key={item.id}
						onClick={() => handleChange(item)}
					>
						{item.label}
					</MenuItem>
				))}
			</Menu>
		</Box>
	);
};

export default TableFilterDropdown;
